import { useEffect, useState, useCallback } from "react";
import { Wallet, FileText, History, CalendarDays } from "lucide-react";
import { rpc } from "@/lib/supabase";
import { PageHeader, Loader, Card, Stat, EmptyState } from "@/components/kit";
import { formatCOP, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const RANGES = [
  { id: "all", label: "Todo" },
  { id: "today", label: "Hoy" },
  { id: "7d", label: "Últimos 7 días" },
  { id: "month", label: "Mes actual" },
  { id: "custom", label: "Rango" },
];
const DOC = {
  PENDING: { t: "Pendiente", c: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
  PARTIAL: { t: "Parcial", c: "text-blue-400 border-blue-500/40 bg-blue-500/10" },
  PAID: { t: "Pagado", c: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" },
};
const Badge = ({ s }) => <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${DOC[s]?.c || ""}`}>{DOC[s]?.t || s}</span>;

function rangeDates(id, custom) {
  const now = new Date();
  if (id === "today") { const d = new Date(now); d.setHours(0,0,0,0); return [d, null]; }
  if (id === "7d") return [new Date(now.getTime() - 7*86400000), null];
  if (id === "month") return [new Date(now.getFullYear(), now.getMonth(), 1), null];
  if (id === "custom") return [custom.from ? new Date(custom.from) : null, custom.to ? new Date(new Date(custom.to).getTime()+86399000) : null];
  return [null, null];
}

export default function BusinessAccount() {
  const [range, setRange] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [summary, setSummary] = useState(null);
  const [charges, setCharges] = useState([]);
  const [payments, setPayments] = useState([]);
  const [view, setView] = useState("charges");

  const load = useCallback(() => {
    const [f, t] = rangeDates(range, custom);
    const p = { p_from: f ? f.toISOString() : null, p_to: t ? t.toISOString() : null };
    Promise.all([
      rpc("get_my_business_account", p),
      rpc("get_my_business_charges", p),
      rpc("get_my_business_payments", p),
    ]).then(([s, c, pay]) => { setSummary(s); setCharges(c || []); setPayments(pay || []); })
      .catch(() => setSummary({}));
  }, [range, custom]);
  useEffect(() => { load(); }, [load]);

  if (!summary) return <Loader />;
  return (
    <div>
      <PageHeader title="Cuenta y pagos" subtitle="Cuánto has facturado, cuánto has pagado y tu saldo pendiente con RAPIFAST." testId="business-account" />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {RANGES.map((r) => (
          <button key={r.id} onClick={() => setRange(r.id)} data-testid={`range-${r.id}`}
            className={`rounded-xl px-3.5 py-1.5 text-xs font-semibold transition-colors ${range === r.id ? "bg-[#FF6A00] text-black" : "border border-[#27272A] text-gray-300 hover:bg-[#18181B]"}`}>
            {r.label}
          </button>
        ))}
        {range === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} className="w-40 border-[#27272A] bg-[#0d0d0f] text-xs" data-testid="range-from" />
            <span className="text-gray-500">→</span>
            <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} className="w-40 border-[#27272A] bg-[#0d0d0f] text-xs" data-testid="range-to" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Facturado" value={formatCOP(summary.facturado)} icon={FileText} testId="acc-facturado" />
        <Stat label="Pagado" value={formatCOP(summary.pagado)} accent="#10B981" icon={Wallet} testId="acc-pagado" />
        <Stat label="Saldo pendiente" value={formatCOP(summary.pendiente)} accent="#FF6A00" testId="acc-pendiente" />
        <Stat label="Servicios por pagar" value={summary.servicios_pendientes} testId="acc-pendientes" />
      </div>

      <div className="mb-3 mt-8 flex gap-2">
        <button onClick={() => setView("charges")} data-testid="view-charges" className={`rounded-xl px-4 py-2 text-sm font-semibold ${view === "charges" ? "bg-[#FF6A00] text-black" : "border border-[#27272A] text-gray-300"}`}>Servicios</button>
        <button onClick={() => setView("payments")} data-testid="view-payments" className={`rounded-xl px-4 py-2 text-sm font-semibold ${view === "payments" ? "bg-[#FF6A00] text-black" : "border border-[#27272A] text-gray-300"}`}>Historial de pagos</button>
      </div>

      {view === "charges" && (
        charges.length === 0 ? <EmptyState icon={FileText} title="Sin servicios en el período" testId="acc-charges-empty" /> : (
          <Card className="p-0" data-testid="acc-charges-list">
            {charges.map((c, i) => (
              <div key={c.charge_id} className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${i > 0 ? "border-t border-[#1e1e21]" : ""}`} data-testid={`charge-row-${c.service_id}`}>
                <div>
                  <div className="text-sm font-medium text-white">#{c.service_id.slice(0,8)} · {formatCOP(c.amount)}</div>
                  <div className="text-xs text-gray-500">{c.pickup_address} → {c.dropoff_address}</div>
                  <div className="text-[11px] text-gray-600">{formatDateTime(c.delivered_at)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right text-xs text-gray-400">Pagado {formatCOP(c.pagado)}<br/>Saldo <span className="font-bold text-white">{formatCOP(c.saldo)}</span></div>
                  <Badge s={c.status} />
                </div>
              </div>
            ))}
          </Card>
        )
      )}

      {view === "payments" && (
        payments.length === 0 ? <EmptyState icon={History} title="Sin pagos registrados" testId="acc-payments-empty" /> : (
          <Card className="p-0" data-testid="acc-payments-list">
            {payments.map((p, i) => (
              <div key={p.id} className={`flex flex-wrap items-center justify-between gap-3 px-5 py-4 ${i > 0 ? "border-t border-[#1e1e21]" : ""}`} data-testid={`payment-row-${p.id}`}>
                <div>
                  <div className="font-heading font-bold text-emerald-400">{formatCOP(p.amount)} <span className="ml-2 text-xs font-normal text-gray-400">{p.method === "CASH" ? "Efectivo" : p.method}</span></div>
                  <div className="text-xs text-gray-500">
                    {formatDateTime(p.created_at)}{p.reference && ` · ${p.reference}`}{p.service_id && ` · #${p.service_id.slice(0,8)}`}
                  </div>
                  {p.registrado_por && <div className="text-[11px] text-gray-600">Registrado por {p.registrado_por}</div>}
                </div>
                {p.kind === "ADJUSTMENT" && <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold text-amber-400">AJUSTE</span>}
              </div>
            ))}
          </Card>
        )
      )}
    </div>
  );
}
